
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { Map, Marker, Popup } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin } from 'lucide-react';

// Remplacez cette clé par votre propre clé publique Mapbox (sans danger côté client)
// Pour une application en production, utilisez des variables d'environnement
const MAPBOX_TOKEN = "pk.eyJ1IjoibG92YWJsZS1kZW1vIiwiYSI6ImNscnRxN2FpczE4eHEya28zNGVnZmN1NmcifQ.AKymYLcWh0UggtdgnGSkBg";

interface ReportMapProps {
  reports: any[];
  onReportClick?: (report: any) => void;
  centerLat?: number;
  centerLng?: number;
}

const ReportMap: React.FC<ReportMapProps> = ({ 
  reports = [], 
  onReportClick,
  centerLat = 48.856614, // Paris par défaut
  centerLng = 2.3522219
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<Map | null>(null);
  const markersRef = useRef<{ [key: string]: Marker }>({});
  const popupsRef = useRef<{ [key: string]: Popup }>({});
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Initialiser la carte
  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    
    try {
      mapboxgl.accessToken = MAPBOX_TOKEN;
      
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [centerLng, centerLat],
        zoom: 13,
        minZoom: 3,
        maxZoom: 18
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      map.current.addControl(new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true
      }), 'top-right');
      
      map.current.on('load', () => {
        setLoading(false);
      });

      map.current.on('error', (e) => {
        console.error("Erreur de carte Mapbox:", e);
        setMapError("La carte n'est pas disponible pour le moment");
        setLoading(false);
      });
      
      // Timeout de sécurité
      setTimeout(() => {
        if (loading) {
          console.warn("Timeout de chargement de la carte");
          setMapError("La carte met trop de temps à charger");
          setLoading(false);
        }
      }, 10000);
    } catch (error) {
      console.error("Erreur d'initialisation de la carte:", error);
      setMapError("La carte n'est pas disponible");
      setLoading(false);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      
      // Nettoyer les marqueurs et popups
      Object.values(markersRef.current).forEach(marker => marker.remove());
      Object.values(popupsRef.current).forEach(popup => popup.remove());
      markersRef.current = {};
      popupsRef.current = {};
    };
  }, [centerLat, centerLng]);

  // Gérer les marqueurs sur la carte
  useEffect(() => {
    if (!map.current || loading || mapError) return;

    // Nettoyer les marqueurs et popups existants
    Object.values(markersRef.current).forEach(marker => marker.remove());
    Object.values(popupsRef.current).forEach(popup => popup.remove());
    markersRef.current = {};
    popupsRef.current = {};

    // Créer de nouveaux marqueurs pour chaque signalement
    const bounds = new mapboxgl.LngLatBounds();
    let hasValidCoordinates = false;

    reports.forEach(report => {
      if (!report.location) return;
      
      try {
        // Analyser les coordonnées (supposant un format "lat,lng")
        const [lat, lng] = report.location.split(',').map(Number);
        if (isNaN(lat) || isNaN(lng)) return;

        // Créer un élément HTML personnalisé pour le marqueur
        const markerEl = document.createElement('div');
        markerEl.className = 'custom-marker';
        markerEl.innerHTML = `
          <div class="flex items-center justify-center w-10 h-10 -mt-10 -ml-5">
            <div class="p-1 rounded-full bg-white shadow-lg">
              <div class="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
            </div>
          </div>
        `;

        // Créer un popup pour le marqueur
        const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
          .setHTML(`
            <div class="p-2 max-w-xs">
              <h3 class="font-bold text-sm">${report.title}</h3>
              <p class="text-xs text-gray-500">${report.category}</p>
            </div>
          `);

        // Créer et ajouter le marqueur
        const marker = new mapboxgl.Marker(markerEl)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(map.current);

        // Ajouter un gestionnaire d'événements de clic
        marker.getElement().addEventListener('click', () => {
          if (onReportClick) {
            onReportClick(report);
          }
        });

        // Stocker le marqueur et le popup pour nettoyage ultérieur
        markersRef.current[report.id] = marker;
        popupsRef.current[report.id] = popup;

        // Étendre les limites pour inclure ce marqueur
        bounds.extend([lng, lat]);
        hasValidCoordinates = true;
      } catch (error) {
        console.warn(`Erreur lors de l'affichage du marqueur pour le rapport ${report.id}:`, error);
      }
    });

    // Ajuster la carte pour montrer tous les marqueurs
    if (hasValidCoordinates) {
      if (reports.length > 1) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
          duration: 1000
        });
      } else if (reports.length === 1) {
        const report = reports[0];
        if (report.location) {
          const [lat, lng] = report.location.split(',').map(Number);
          if (!isNaN(lat) && !isNaN(lng)) {
            map.current.flyTo({
              center: [lng, lat],
              zoom: 15,
              duration: 1000
            });
          }
        }
      }
    }
  }, [reports, loading, mapError, onReportClick]);

  if (mapError) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-muted/50 rounded-lg border min-h-[400px]">
        <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground text-sm text-center mb-4">
          {mapError}
        </p>
        <p className="text-xs text-muted-foreground">
          La carte sera disponible prochainement
        </p>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden border border-gray-100 shadow-sm">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-gray-500">Chargement de la carte...</p>
          </div>
        </div>
      )}
      <div 
        ref={mapContainer} 
        className="w-full h-[500px]"
      />
    </div>
  );
};

export default ReportMap;
